import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from '@quorum/api';
import { ServiceGroup, ServiceGroupCategory } from '@quorum/models/xs-resource';
import { combineLatest } from 'rxjs';
import { debounceTime, startWith } from 'rxjs/operators';
import { LinkedDmsService, MotorServiceCategory, MotorServiceMapping } from './motor-taxonomy.service';

export interface MotorMappingEditDialogData {
  item: MotorServiceMapping;
  category: MotorServiceCategory;
  serviceGroups: ServiceGroup[];
  rooftopId: number;
}

export type MotorMappingEditDialogResult = { action: 'save'; linkedServices: LinkedDmsService[] } | { action: 'unmap' };

export interface ServiceGroupSection {
  categoryId: number | null;
  categoryName: string;
  items: ServiceGroup[];
}

const SELECTED_SECTION_ID = -999;

@Component({
  selector: 'svc-motor-mapping-edit-dialog',
  templateUrl: './motor-mapping-edit-dialog.component.html',
  styleUrls: ['./motor-mapping-edit-dialog.component.scss'],
})
export class MotorMappingEditDialogComponent implements OnInit {
  searchControl = new FormControl('');
  categoryControl = new FormControl('');

  displayedGroups: ServiceGroup[] = [];
  groupedSections: ServiceGroupSection[] = [];
  selectedGroups: Map<string, ServiceGroup> = new Map();
  loadedCategories: ServiceGroupCategory[] = [];

  // Op codes from existing mappings that don't match any current ServiceGroup.
  // Preserved so that saving doesn't silently delete them.
  private orphanedLinkedServices: LinkedDmsService[] = [];

  private categoryMap = new Map<number, string>();

  get isMapped(): boolean {
    return this.data.item.linkedServices.length > 0;
  }

  get hasSelections(): boolean {
    return this.selectedGroups.size > 0 || this.orphanedLinkedServices.length > 0;
  }

  get selectedCount(): number {
    return this.selectedGroups.size + this.orphanedLinkedServices.length;
  }

  get canSave(): boolean {
    return this.hasSelections || this.isMapped;
  }

  constructor(
    public dialogRef: MatDialogRef<MotorMappingEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MotorMappingEditDialogData,
    private apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.displayedGroups = this.ownAndSharedGroups(this.data.serviceGroups);
    this.recomputeGroupedSections();

    for (const linked of this.data.item.linkedServices) {
      const sg = this.data.serviceGroups.find((s) => s.workOrderLocationOpCode === linked.opCode);
      if (sg) {
        this.selectedGroups.set(sg.id, sg);
      } else {
        this.orphanedLinkedServices.push(linked);
      }
    }
    this.recomputeGroupedSections();

    this.apiService
      .get('v/1/fixed-operations/service-group-categories', {
        params: { rooftopId: `${this.data.rooftopId},-1` },
      })
      .subscribe((cats: ServiceGroupCategory[]) => {
        this.loadedCategories = cats
          .filter((c) => c.rooftopId === this.data.rooftopId)
          .sort((a, b) => a.description.localeCompare(b.description));
        this.categoryMap = new Map(this.loadedCategories.map((c) => [c.id, c.description]));
        this.recomputeGroupedSections();
      });

    combineLatest([
      this.searchControl.valueChanges.pipe(startWith('')),
      this.categoryControl.valueChanges.pipe(startWith('')),
    ])
      .pipe(debounceTime(200))
      .subscribe(([search, catId]) => {
        const numCatId = catId ? Number(catId) : null;
        const base = this.ownAndSharedGroups(this.data.serviceGroups);
        this.displayedGroups = base.filter((sg) => {
          const matchesSearch =
            !search ||
            (sg.workOrderLocationOpCode ?? '').toLowerCase().includes(search.toLowerCase()) ||
            (sg.name ?? '').toLowerCase().includes(search.toLowerCase());
          const matchesCategory = !numCatId || sg.categoryId === numCatId;
          return matchesSearch && matchesCategory;
        });
        this.recomputeGroupedSections();
      });
  }

  private ownAndSharedGroups(groups: ServiceGroup[]): ServiceGroup[] {
    return groups.filter((sg) => sg.rooftopId === this.data.rooftopId || sg.rooftopId === -1);
  }

  private recomputeGroupedSections(): void {
    const sections: ServiceGroupSection[] = [];

    // SELECTED section at the top — uses selectedGroups directly so items remain visible
    // even when search/category filters are active
    if (this.selectedGroups.size > 0) {
      sections.push({
        categoryId: SELECTED_SECTION_ID,
        categoryName: `SELECTED (${this.selectedGroups.size})`,
        items: Array.from(this.selectedGroups.values()),
      });
    }

    // Normal category sections, excluding selected items
    for (const sg of this.displayedGroups) {
      if (this.selectedGroups.has(sg.id)) continue;
      const catName = this.categoryMap.get(sg.categoryId) ?? 'Other';
      let section = sections.find((s) => s.categoryId === sg.categoryId);
      if (!section) {
        section = { categoryId: sg.categoryId, categoryName: catName, items: [] };
        sections.push(section);
      }
      section.items.push(sg);
    }

    this.groupedSections = sections;
  }

  toggleGroup(group: ServiceGroup): void {
    if (this.selectedGroups.has(group.id)) {
      this.selectedGroups.delete(group.id);
    } else {
      this.selectedGroups.set(group.id, group);
    }
    this.recomputeGroupedSections();
  }

  isSelected(group: ServiceGroup): boolean {
    return this.selectedGroups.has(group.id);
  }

  removeSelected(groupId: string): void {
    this.selectedGroups.delete(groupId);
    this.recomputeGroupedSections();
  }

  unlinkAll(): void {
    this.selectedGroups.clear();
    this.orphanedLinkedServices = [];
    this.recomputeGroupedSections();
  }

  isSelectedSection(section: ServiceGroupSection): boolean {
    return section.categoryId === SELECTED_SECTION_ID;
  }

  save(): void {
    if (!this.canSave) return;

    if (this.hasSelections) {
      const fromSelected: LinkedDmsService[] = Array.from(this.selectedGroups.values())
        .filter((sg) => !!sg.workOrderLocationOpCode)
        .map((sg) => ({
          opCode: sg.workOrderLocationOpCode,
          serviceGroupName: sg.name ?? '',
        }));
      const linkedServices = this.orphanedLinkedServices.concat(fromSelected);
      this.dialogRef.close({ action: 'save', linkedServices } as MotorMappingEditDialogResult);
    } else {
      this.dialogRef.close({ action: 'unmap' } as MotorMappingEditDialogResult);
    }
  }

  trackById(_index: number, item: ServiceGroup): string {
    return item.id;
  }

  trackByCategoryId(_index: number, section: ServiceGroupSection): number | null {
    return section.categoryId;
  }
}
